import moment from "moment";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, Icon } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { APPOINTMENT_RECALL_STATUSES } from "models";
import { canWriteBackCustomerData } from "utils/appointmentUtils";
import { toDate, toISOString } from "utils/dateHelperFunctions";

import type { AppointmentSection } from "modules/Appointments/components/NewAppointment";

type AppointmentFormSectionProps = {
  hasErrors: boolean;
  isGetWoNrSuccess: boolean;
  appointmentWoNr?: string;
};

const FormHelper = createFormHelper<AppointmentSection>();

export const AppointmentFormSection = ({ hasErrors, isGetWoNrSuccess, appointmentWoNr }: AppointmentFormSectionProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { setValue, watch } = FormHelper.useFormContext();
  const [regNr, woNr, timeCarApp] = watch(["car.reg_nr", "wo_nr", "time_car_app"]);

  const hasWriteBackPermissionForCustomerData = canWriteBackCustomerData(selectedLocation);

  useEffect(() => {
    if (!isGetWoNrSuccess) return;

    setValue("wo_nr", appointmentWoNr || "");
    setValue("due_in", new Date());
    setValue("time_car_app", new Date());
    setValue("has_recalls_data", APPOINTMENT_RECALL_STATUSES.None);
    setValue("car_return_time", new Date(Date.now() + 60 * 1000).toISOString());
  }, [isGetWoNrSuccess]);

  const handleOnChangeCarReturnTime = (date: Date | null) => {
    const selectedTime = moment(date);
    const inTime = moment(timeCarApp);
    const validTime = selectedTime.isSameOrBefore(inTime) ? inTime.clone().add(1, "minute") : selectedTime;

    setValue("car_return_time", validTime.toISOString());
  };

  const handleOnChangeDueIn = (date: Date | null) => setValue("time_car_app", date ? new Date(date) : new Date());

  const handleOnAppointmentDateChange = (date: Date | null) => {
    setValue("car_return_time", toISOString(date));
    if (date) setValue("time_car_app", date);
  };

  return (
    <>
      <Form.Group className="mt-25 NewAppointment__CarDetails ">
        <label className="big">
          <Icon className="list" />
          {t("v8_appointment", "Appointment")}
        </label>
      </Form.Group>
      <Form.Group className="NewAppointment__CarDetails">
        <Form.Field width={8} required error={hasErrors && !timeCarApp}>
          <label>{t("v8_appointment_date", "Appointment date *")}</label>
          <FormHelper.Field.DatePickerRaw
            name="due_in"
            formFieldProps={{ fluid: true }}
            disabled={!regNr}
            showYearDropdown
            dateFormat={DATE_FORMATS_FNS.dateMonthYear}
            minDate={toDate(new Date())}
            todayButton={t("v8_today", "Today")}
            placeholder={t("v8_appointment_date")}
            onChange={handleOnAppointmentDateChange}
          />
        </Form.Field>
        {!hasWriteBackPermissionForCustomerData && (
          <Form.Field width={8} required error={hasErrors && !woNr}>
            <label>{t("v8_wo", "WO")}</label>
            <FormHelper.Field.Input
              name="wo_nr"
              inputProps={{
                placeholder: t("v8_wo", "WO"),
                fluid: true,
                disabled: true
              }}
            />
          </Form.Field>
        )}
      </Form.Group>
      <Form.Group className="NewAppointment__CarDetails">
        <Form.Field width={8} required>
          <label>{t("v8_scheduled_in_date", "Scheduled-in date")}</label>
          <FormHelper.Field.DatePickerRaw
            name="time_car_app"
            formFieldProps={{ fluid: true }}
            showTimeSelect
            showTimeSelectOnly
            disabled={!regNr}
            dateFormat={DATE_FORMATS_FNS.dateMonthYearTime}
            minDate={toDate(new Date())}
            placeholder={t("v8_scheduled_in_date")}
            onChange={handleOnChangeDueIn}
          />
        </Form.Field>
        <Form.Field width={8} required>
          <label>{t("v8_scheduled_out", "Scheduled-out")}</label>
          <FormHelper.Field.DatePickerRaw
            name="car_return_time"
            formFieldProps={{ fluid: true }}
            showTimeInput
            showYearDropdown
            showMonthDropdown
            disabled={!regNr}
            dateFormat={DATE_FORMATS_FNS.dateMonthYearTime}
            todayButton={t("v8_today", "Today")}
            placeholder={t("v8_scheduled_out", "Scheduled out")}
            minDate={moment(timeCarApp).add(15, "minute").toDate()}
            onChange={handleOnChangeCarReturnTime}
          />
        </Form.Field>
      </Form.Group>
    </>
  );
};
